import medicationImageUrl from "@/assets/medication.svg"
import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import HeroImage from "@/components/HeroImage"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import {
  useGetMedicationRemindersQuery,
  useSelfQuery,
  useSetMedicationRemindersMutation,
} from "@/data/queries"
import MedicationReminders from "@/screens/onboarding/components/MedicationReminders"
import { useFinishOnboarding } from "@/util/hooks/useFinishOnboarding"
import { useState } from "react"

type MedicationRemindersStepProps = {
  onBack: () => void
}

type MedicationRemindersFormProps = {
  patientId: string
  initialReminderTimes: string[]
}

// Split out so `initialReminderTimes` seeds useState on mount only — later
// refetches must not clobber in-progress edits.
const MedicationRemindersForm = ({
  patientId,
  initialReminderTimes,
}: MedicationRemindersFormProps) => {
  const { mutateAsync: setReminders, isPending: isSavingReminders } =
    useSetMedicationRemindersMutation()
  const { finishOnboarding, isPending: isFinishing } = useFinishOnboarding()

  const [reminderTimes, setReminderTimes] = useState<string[]>(initialReminderTimes)

  const isPending = isSavingReminders || isFinishing

  const handleSubmit = async () => {
    await setReminders({ patientId, reminderTimes })
    await finishOnboarding()
  }

  return (
    <>
      <MedicationReminders reminderTimes={reminderTimes} onChange={setReminderTimes} />

      <Button
        type="button"
        disabled={isPending}
        className="mt-auto flex w-full gap-3 py-2 text-base"
        onClick={handleSubmit}
      >
        {isPending && <Spinner />}
        Save and continue
      </Button>
    </>
  )
}

const MedicationRemindersStep = ({ onBack }: MedicationRemindersStepProps) => {
  const { data: self } = useSelfQuery()
  const patientId = self?.id ?? ""

  const { data: existingReminders, isPending: isLoadingReminders } = useGetMedicationRemindersQuery(
    { patientId },
  )

  return (
    <Page showAppbar={false}>
      <div className="absolute left-4 top-4 z-10">
        <BackButton customClickHandler={onBack} />
      </div>
      <HeroImage src={medicationImageUrl} alt="Medication" />
      <div className="flex min-h-[calc(100vh-40vh-56px)] flex-col gap-6">
        <p className="text-base text-primary-contrast">
          What time(s) each day would you want to be reminded to take your medication?
        </p>

        {isLoadingReminders || existingReminders === undefined ? (
          <div className="flex justify-center py-4">
            <Spinner />
          </div>
        ) : (
          <MedicationRemindersForm patientId={patientId} initialReminderTimes={existingReminders} />
        )}
      </div>
    </Page>
  )
}

export default MedicationRemindersStep
